import type { ChannelClient } from '@hibro/platform-core';
import type {
  ILauncherService,
  IModuleService,
  IProductConfigService,
  IUpdateService,
  IWhenContextService,
} from '@hibro/shared';
import { cn } from '@hibro/ui-client';
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import type { OpenExternalBridge } from '../app-links/open';
import type { WindowControlsBridge } from '../components/custom-header/custom-header';
import { useT } from '../i18n/i18n';
import type { NavigationSource } from '../navigation/navigation';
import { isSettingsWindowSurface } from '../panels/settings-window/ids';
import type { LoadUi } from '../runtime/ui-registry';
import type { DetachBridge } from './detach';
import { DetachedSurface } from './detached-surface';
import type { ExternalBridge } from './external-surface';
import type { IframeBridge } from './iframe-surface';
import {
  FALLBACK_MIN_HEIGHT,
  FALLBACK_MIN_WIDTH,
  FRAME_BAR_HEIGHT,
  type FrameBox,
  initialFrameBox,
  moveFrame,
  resizeFrame,
  type Viewport,
} from './inline-detached-geometry';
import type {
  InlineDetachedStore,
  InlineDetachedSurface,
} from './inline-detached-store';
import {
  focusHostPage,
  OPEN_MODAL_FOCUS_REJECTED,
} from './overlay-window-focus';

/** The viewport the frames live in — this layer fills it exactly. */
const viewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export interface InlineDetachedHostProps {
  store: InlineDetachedStore;
  moduleService: IModuleService;
  uiLoaders: Map<string, LoadUi>;
  client: ChannelClient;
  iframeBridge?: IframeBridge;
  externalBridge?: ExternalBridge;
  navigation?: NavigationSource;
  detachBridge?: DetachBridge;
  /** Old platform settings body, mounted only on the preferences window. */
  developerSettings?: ReactNode;
  /** About page on the preferences window. */
  updateService?: IUpdateService;
  launcherService?: ILauncherService;
  productConfigService?: IProductConfigService;
  openExternalBridge?: OpenExternalBridge;
  /** Slash-command page on the preferences window. */
  whenContextService?: IWhenContextService;
  /** 页内模拟窗没能被 `window.focus()` 叫到前台时记一条。 */
  log?: (message: string, data?: Record<string, unknown>) => void;
}

/**
 * Floating host for detached surfaces this runtime could not give a window of
 * their own (架构方案 §5.8 通用 Detachable, 见 18 弹窗拦截).
 *
 * Renders each surface through the very same {@link DetachedSurface} a real
 * detached window uses, so a fragment behaves identically whichever placement it
 * got. What this adds is the part an OS window would have provided: a frame that
 * can be moved, resized, collapsed and closed.
 *
 * Which is also why the inner surface is always mounted **bare** regardless of
 * the request's `chrome`: this frame *is* the window chrome. A browser has no
 * `-webkit-app-region`, so `CustomHeader`'s drag region would be inert here and
 * its window controls report `chrome: 'none'` anyway — leaving a bar that can
 * neither move nor control the window. Closing the frame is the attach-back that
 * bar would have offered.
 *
 * The content pane is a containing block for `position: fixed` (`transform`
 * below). A bare surface fills its window with `inset: 0`; without that block
 * the box is the viewport and the surface paints over this frame's title bar.
 */
export function InlineDetachedHost({
  store,
  moduleService,
  uiLoaders,
  client,
  iframeBridge,
  externalBridge,
  navigation,
  detachBridge,
  developerSettings,
  updateService,
  launcherService,
  productConfigService,
  openExternalBridge,
  whenContextService,
  log,
}: InlineDetachedHostProps): ReactNode {
  const surfaces = useSyncExternalStore(
    (onChange) => store.subscribe(onChange),
    () => store.list(),
  );

  if (surfaces.length === 0) {
    return null;
  }

  return (
    // Above the overlay stack (`z-50` in `@hibro/ui-client`): this stands in for
    // an OS window, which no in-app modal can cover.
    <div className="pointer-events-none fixed inset-0 z-60">
      {surfaces.map((surface, index) => (
        <InlineDetachedFrame
          key={surface.id}
          surface={surface}
          cascade={index}
          onRaise={() => store.raise(surface.id)}
          onClose={() => store.unmount(surface.id)}
          moduleService={moduleService}
          uiLoaders={uiLoaders}
          client={client}
          iframeBridge={iframeBridge}
          externalBridge={externalBridge}
          navigation={navigation}
          detachBridge={detachBridge}
          whenContextService={whenContextService}
          developerSettings={
            isSettingsWindowSurface(surface.viewId, surface.fragmentId)
              ? developerSettings
              : undefined
          }
          updateService={updateService}
          launcherService={launcherService}
          productConfigService={productConfigService}
          openExternalBridge={openExternalBridge}
          log={log}
        />
      ))}
    </div>
  );
}

interface InlineDetachedFrameProps extends Omit<
  InlineDetachedHostProps,
  'store'
> {
  surface: InlineDetachedSurface;
  /** Position in the stack at mount time, so frames do not perfectly overlap. */
  cascade: number;
  onRaise: () => void;
  onClose: () => void;
}

function InlineDetachedFrame({
  surface,
  cascade,
  onRaise,
  onClose,
  moduleService,
  uiLoaders,
  client,
  iframeBridge,
  externalBridge,
  navigation,
  detachBridge,
  developerSettings,
  updateService,
  launcherService,
  productConfigService,
  openExternalBridge,
  whenContextService,
  log,
}: InlineDetachedFrameProps): ReactNode {
  const t = useT();
  const resizable = surface.resizable !== false;
  const minSize = {
    width: surface.minWidth ?? FALLBACK_MIN_WIDTH,
    height: surface.minHeight ?? FALLBACK_MIN_HEIGHT,
  };

  const [box, setBox] = useState<FrameBox>(() =>
    initialFrameBox(surface, cascade, viewport()),
  );
  const [maximized, setMaximized] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const maximizeListeners = useRef(new Set<(value: boolean) => void>());

  const setMaximizedAndNotify = useCallback((next: boolean) => {
    setMaximized(next);
    for (const listener of [...maximizeListeners.current]) {
      listener(next);
    }
  }, []);

  /**
   * The window controls a bare fragment self-paints against (§5.8): its own
   * close/minimize buttons must work here exactly as they do in a real window.
   * `chrome: 'none'` is the browser's reading and goes unused — the inner
   * surface is bare, so `CustomHeader` never renders.
   */
  const windowControls = useMemo<WindowControlsBridge>(
    () => ({
      chrome: 'none',
      minimize: () => setCollapsed(true),
      toggleMaximize: () => setMaximizedAndNotify(!maximized),
      close: onClose,
      isMaximized: () => Promise.resolve(maximized),
      onMaximizeChange: (listener) => {
        maximizeListeners.current.add(listener);
        return () => maximizeListeners.current.delete(listener);
      },
      // 内联模拟窗口嵌在宿主页面里：以页面级 window 失焦/聚焦模拟窗口失活/激活。
      onWindowBlur: (listener) => {
        const onBlur = () => listener(true);
        const onFocus = () => listener(false);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        return () => {
          window.removeEventListener('blur', onBlur);
          window.removeEventListener('focus', onFocus);
        };
      },
      // 内联模拟窗口无真全屏/置顶：全屏映射到最大化，置顶 no-op，尺寸走 setBox。
      toggleFullScreen: () => setMaximizedAndNotify(!maximized),
      isFullScreen: () => Promise.resolve(maximized),
      setAlwaysOnTop: () => undefined,
      isAlwaysOnTop: () => Promise.resolve(false),
      setSize: (width, height) => setBox((b) => ({ ...b, width, height })),
      // 内联模拟窗口没有 OS 层最小尺寸约束（真实约束只在桌面主进程生效）：no-op。
      setMinimumSize: () => undefined,
      getSize: () => Promise.resolve({ width: box.width, height: box.height }),
      // 内联模拟窗口无真实 OS 位置：置位/居中都 no-op（同 setAlwaysOnTop）。
      setPosition: () => undefined,
      present: () => {
        if (!focusHostPage()) {
          log?.(OPEN_MODAL_FOCUS_REJECTED, {
            reason: 'the page stayed in the background',
          });
        }
        return Promise.resolve(undefined);
      },
    }),
    [maximized, onClose, setMaximizedAndNotify, box, log],
  );

  // Dragging and resizing share one gesture: press, follow the pointer, release.
  // Listeners go on `window` rather than the frame so a fast pointer that leaves
  // the frame mid-drag keeps driving it.
  const startGesture = (
    event: ReactPointerEvent,
    apply: (dx: number, dy: number, from: FrameBox) => FrameBox,
  ): void => {
    if (event.button !== 0 || maximized) {
      return;
    }
    event.preventDefault();
    onRaise();
    const from = box;
    const originX = event.clientX;
    const originY = event.clientY;
    const move = (moveEvent: globalThis.PointerEvent): void => {
      setBox(
        apply(moveEvent.clientX - originX, moveEvent.clientY - originY, from),
      );
    };
    const stop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  const onDragStart = (event: ReactPointerEvent): void => {
    startGesture(event, (dx, dy, from) => moveFrame(from, dx, dy, viewport()));
  };

  const onResizeStart = (event: ReactPointerEvent): void => {
    startGesture(event, (dx, dy, from) => resizeFrame(from, dx, dy, minSize));
  };

  // Maximized geometry is relative, not measured: the parent layer already fills
  // the viewport, so percentages follow a window resize while pixels frozen at
  // render time would not.
  const geometry: CSSProperties = maximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: collapsed ? FRAME_BAR_HEIGHT : '100%',
      }
    : {
        left: box.x,
        top: box.y,
        width: box.width,
        height: collapsed ? FRAME_BAR_HEIGHT : box.height,
      };

  return (
    <div
      data-slot="inline-detached-frame"
      data-surface={surface.id}
      className="pointer-events-auto absolute flex flex-col overflow-hidden rounded-lg border bg-card shadow-2xl"
      style={geometry}
      onPointerDownCapture={onRaise}
    >
      {/* The affordances an OS window frame would have carried. Kept slim: the
          surface inside was written to fill a window, not to share it. */}
      <div
        className={cn(
          'flex shrink-0 items-center justify-end gap-0.5 border-b bg-muted/60 px-1',
          maximized ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        )}
        style={{ height: FRAME_BAR_HEIGHT }}
        onPointerDown={onDragStart}
        onDoubleClick={() => resizable && setMaximizedAndNotify(!maximized)}
      >
        <FrameButton
          label={
            collapsed
              ? t('detached.inline.expand')
              : t('detached.inline.collapse')
          }
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? '▣' : '—'}
        </FrameButton>
        {resizable ? (
          <FrameButton
            label={
              maximized
                ? t('detached.inline.restore')
                : t('detached.inline.maximize')
            }
            onClick={() => setMaximizedAndNotify(!maximized)}
          >
            {maximized ? '❐' : '□'}
          </FrameButton>
        ) : null}
        <FrameButton label={t('detached.attachBack')} onClick={onClose}>
          ✕
        </FrameButton>
      </div>
      {collapsed ? null : (
        <div
          className="relative min-h-0 flex-1"
          // Any non-none transform makes this the containing block for fixed
          // descendants (CSS transforms). `translate(0)` adds no offset.
          style={{ transform: 'translate(0)' }}
        >
          <DetachedSurface
            viewId={surface.viewId}
            moduleId={surface.moduleId}
            fragmentId={surface.fragmentId}
            instanceKey={surface.instanceKey}
            payload={surface.payload}
            chrome={false}
            moduleService={moduleService}
            uiLoaders={uiLoaders}
            client={client}
            iframeBridge={iframeBridge}
            externalBridge={externalBridge}
            navigation={navigation}
            windowControlsBridge={windowControls}
            detachBridge={detachBridge}
            developerSettings={developerSettings}
            updateService={updateService}
            launcherService={launcherService}
            productConfigService={productConfigService}
            openExternalBridge={openExternalBridge}
            whenContextService={whenContextService}
          />
        </div>
      )}
      {resizable && !maximized && !collapsed ? (
        <div
          className="absolute right-0 bottom-0 size-4 cursor-nwse-resize"
          onPointerDown={onResizeStart}
        />
      ) : null}
    </div>
  );
}

function FrameButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className="flex size-5 items-center justify-center rounded text-[10px] leading-none text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      // The bar drags on pointerdown; a button press must not also move the frame.
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
